/**
 * CreateShareLinkSheet Component (Story 14.2)
 *
 * Centered MODAL DIALOG for generating a new portal share link. Mirrors the
 * AddClientSheet visual pattern (Radix Dialog with motion overlay + content)
 * — per UI design directive, all forms in the app open as centered modals,
 * NOT as side sheets.
 *
 * Form state via useState, motion-staggered fields, mutation via TanStack Query.
 *
 * On success the parent receives the freshly minted link (with raw token+url)
 * via onCreated and is responsible for opening TokenRevealDialog. The dialog
 * itself does not display the URL — security responsibility is centralised in
 * TokenRevealDialog.
 *
 * Filename retains the historical "...Sheet" suffix for git-history continuity;
 * the implementation underneath is a Dialog. New components MUST follow the
 * "...Dialog" naming convention.
 */

import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Button,
  Input,
  RadioGroup,
  RadioGroupItem,
} from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import {
  createPortalLink,
  type CreatePortalLinkInput,
  type PortalLinkExpirationPreset,
  type PortalLinkWithToken,
} from "../../lib/portalLinksApi";
import { apiGet } from "../../lib/apiClient";
import type { ToastType } from "../../hooks/useToast";
import { PortalAccessControlsCard } from "./PortalAccessControlsCard";
import { SelectPicker } from "./SelectPicker";

interface CreateShareLinkSheetProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (link: PortalLinkWithToken) => void;
  showToast: (type: ToastType, message: string) => void;
}

interface ReportSummary {
  id: string;
  title: string;
}

const EXPIRATION_OPTIONS: Array<{
  value: PortalLinkExpirationPreset;
  label: string;
}> = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "never", label: "Never expires" },
];

export function CreateShareLinkSheet({
  clientId,
  open,
  onOpenChange,
  onCreated,
  showToast,
}: CreateShareLinkSheetProps) {
  const [label, setLabel] = useState("");
  const [scope, setScope] = useState<"full" | "report">("full");
  const [reportId, setReportId] = useState<string>("");
  const [expiresAt, setExpiresAt] =
    useState<PortalLinkExpirationPreset>("30d");
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [hiddenMetrics, setHiddenMetrics] = useState<string[]>([]);
  const [hiddenIntegrations, setHiddenIntegrations] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset on open (NOT on close) — resetting on close would flash the form to
  // empty values mid-exit-animation. Resetting on open guarantees a clean slate
  // every time the dialog appears without disturbing the close transition.
  useEffect(() => {
    if (open) {
      setLabel("");
      setScope("full");
      setReportId("");
      setExpiresAt("30d");
      setUsePassword(false);
      setPassword("");
      setHiddenMetrics([]);
      setHiddenIntegrations([]);
      setErrors({});
    }
  }, [open]);

  // Lazy-load reports only when scope=report is selected
  const { data: reports, isLoading: reportsLoading } = useQuery<
    ReportSummary[]
  >({
    queryKey: ["reports", "byClient", clientId],
    enabled: scope === "report" && open,
    queryFn: async () => {
      const res = await apiGet(
        `/api/reports?clientId=${encodeURIComponent(clientId)}`
      );
      if (!res.ok) {
        const json = (await res.json().catch(() => ({}))) as {
          detail?: string;
          title?: string;
        };
        throw new Error(json.detail ?? json.title ?? "Failed to load reports");
      }
      const json = (await res.json()) as { data: ReportSummary[] };
      return json.data;
    },
  });

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (label.trim().length === 0) next.label = "Label is required";
    if (label.length > 120) next.label = "Label must be 120 characters or less";
    if (scope === "report" && !reportId) next.reportId = "Select a report";
    if (usePassword && password.length < 8) {
      next.password = "Password must be at least 8 characters";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const create = useMutation({
    mutationFn: () => {
      const base = {
        clientId,
        label: label.trim(),
        expiresAt,
        ...(usePassword && password ? { password } : {}),
        // Story 14.4 — only include arrays when non-empty so the wire shape
        // stays minimal for the common "no filters" case.
        ...(hiddenMetrics.length > 0 ? { hiddenMetrics } : {}),
        ...(hiddenIntegrations.length > 0 ? { hiddenIntegrations } : {}),
      };
      const input: CreatePortalLinkInput =
        scope === "report"
          ? { ...base, scope: "report", reportId }
          : { ...base, scope: "full" };
      return createPortalLink(input);
    },
    onSuccess: (link) => onCreated(link),
    onError: (err: Error & { apiError?: { errors?: Array<{ field: string; message: string }> } }) => {
      const fieldErrors = err.apiError?.errors;
      if (Array.isArray(fieldErrors)) {
        const map: Record<string, string> = {};
        for (const e of fieldErrors) map[e.field] = e.message;
        setErrors(map);
      } else {
        showToast("error", err.message ?? "Failed to create share link");
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    create.mutate();
  }

  // Block close while submitting (avoids losing in-flight state).
  function handleOpenChange(next: boolean): void {
    if (!next && create.isPending) return;
    onOpenChange(next);
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            {/* Overlay — same dim as AddClientSheet for visual parity. */}
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            {/* Content — same width as AddClient (max-w-md) for visual parity. */}
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Generate share link
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    Create a portal URL to share this dashboard or a specific
                    report. You'll see the URL once after generation.
                  </DialogPrimitive.Description>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <label htmlFor="link-label" className="text-sm font-medium">
              Label <span className="text-destructive">*</span>
              {errors.label && (
                <span className="text-sm text-destructive font-normal ml-1" role="alert">
                  {errors.label}
                </span>
              )}
            </label>
            <Input
              id="link-label"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                if (errors.label) setErrors((prev) => ({ ...prev, label: "" }));
              }}
              placeholder="e.g. Q1 dashboard for CEO"
              className={`rounded-lg ${errors.label ? "border-destructive" : ""}`}
              maxLength={120}
              data-testid="label-input"
              autoFocus
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <label className="text-sm font-medium">Scope</label>
            {/*
              `touch-target-sm` opts the radio out of the global
              `button { min-width: 44px; min-height: 44px }` rule that would
              otherwise inflate the 16px circle to 44px.
            */}
            <RadioGroup
              value={scope}
              onValueChange={(v) => setScope(v as "full" | "report")}
              className="grid grid-cols-2 gap-3"
            >
              <label
                htmlFor="scope-full"
                className={`flex items-center gap-2.5 rounded-lg border-[0.5px] px-3 h-11 text-sm cursor-pointer shadow-xs transition-all ${
                  scope === "full"
                    ? "border-primary bg-primary/5"
                    : "border-input bg-background hover:bg-muted/30"
                }`}
              >
                <RadioGroupItem
                  value="full"
                  id="scope-full"
                  className="touch-target-sm h-4 w-4"
                />
                Full dashboard
              </label>
              <label
                htmlFor="scope-report"
                className={`flex items-center gap-2.5 rounded-lg border-[0.5px] px-3 h-11 text-sm cursor-pointer shadow-xs transition-all ${
                  scope === "report"
                    ? "border-primary bg-primary/5"
                    : "border-input bg-background hover:bg-muted/30"
                }`}
              >
                <RadioGroupItem
                  value="report"
                  id="scope-report"
                  className="touch-target-sm h-4 w-4"
                />
                Specific report
              </label>
            </RadioGroup>
          </motion.div>

          {scope === "report" && (
            <motion.div
              className="space-y-2"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <label htmlFor="report-select" className="text-sm font-medium">
                Report <span className="text-destructive">*</span>
                {errors.reportId && (
                  <span className="text-sm text-destructive font-normal ml-1" role="alert">
                    {errors.reportId}
                  </span>
                )}
              </label>
              <SelectPicker
                id="report-select"
                value={reportId}
                onChange={(v) => {
                  setReportId(v);
                  if (errors.reportId) setErrors((prev) => ({ ...prev, reportId: "" }));
                }}
                options={(reports ?? []).map((r) => ({ value: r.id, label: r.title }))}
                placeholder="Select a report"
                loading={reportsLoading}
                loadingLabel="Loading reports…"
                hasError={Boolean(errors.reportId)}
                testId="report-select"
              />
            </motion.div>
          )}

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <label htmlFor="expires-select" className="text-sm font-medium">
              Expiration
            </label>
            <SelectPicker
              id="expires-select"
              value={expiresAt}
              onChange={(v) => setExpiresAt(v as PortalLinkExpirationPreset)}
              options={EXPIRATION_OPTIONS}
              testId="expires-select"
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="use-password"
                checked={usePassword}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUsePassword(checked);
                  // Clear any typed password when toggling OFF — don't keep the
                  // secret lingering in React state (defense in depth; the value
                  // would otherwise persist across re-checks).
                  if (!checked) setPassword("");
                }}
                className="rounded"
                data-testid="use-password"
              />
              <label
                htmlFor="use-password"
                className="text-sm cursor-pointer"
              >
                Require a password
              </label>
            </div>
            {usePassword && (
              <Input
                id="link-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                className={`rounded-lg ${errors.password ? "border-destructive" : ""}`}
                minLength={8}
                data-testid="password-input"
              />
            )}
            {errors.password && (
              <p className="text-sm text-destructive" role="alert">
                {errors.password}
              </p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <PortalAccessControlsCard
              clientId={clientId}
              hiddenMetrics={hiddenMetrics}
              hiddenIntegrations={hiddenIntegrations}
              onChange={(next) => {
                setHiddenMetrics(next.hiddenMetrics);
                setHiddenIntegrations(next.hiddenIntegrations);
              }}
              disabled={create.isPending}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
              <Button
                type="button"
                variant="ghost"
                className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                style={{ border: "0.5px solid hsl(var(--input))" }}
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="brand"
                className="rounded-xl cursor-pointer"
                disabled={create.isPending}
                data-testid="submit-create-link"
              >
                {create.isPending ? "Generating…" : "Generate link"}
              </Button>
            </div>
          </motion.div>
                </form>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
